<template>
  <div id="whale-backtester-signals-tab">
    <helpers-section-header v-show="!isSignalView" class="mt-0 mb-6">
      Choose Signal Sources
    </helpers-section-header>

    <!-- Signals -->
    <v-window
      v-if="whaleTraderCriteria && whaleTraderCriteria.signals"
      v-model="isSignalView"
      touchless
    >
      <!-- Signals: List -->
      <v-window-item :value="false">
        <v-expansion-panels
          v-model="selectedSignalsIndexes"
          accordion
          multiple
          readonly
          class="d-inline-block"
          style="width: auto"
        >
          <v-expansion-panel
            v-for="signal in Object.values(whaleTraderCriteria.signals)"
            :key="signal.signal_id"
            class="mb-2"
          >
            <v-expansion-panel-title hide-actions style="cursor: default">
              <v-row no-gutters align="center">
                <v-col>
                  <v-switch v-model="signal.enabled" color="primary" hide-details class="mt-0" />
                </v-col>
                <v-col cols="10">
                  <h3 class="text-h5 mb-0">{{ signal.name }} Signals</h3>
                  <div class="text-justify text-body-2">
                    {{ signal.description }}
                  </div>
                </v-col>
              </v-row>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <v-btn
                :disabled="!signal.enabled"
                class="d-flex justify-space-between"
                theme="dark"
                color="primary"
                variant="outlined"
                @click="setCurrentSignalView(signal.signal_id)"
              >
                Configure
              </v-btn>
              <v-radio-group v-show="signal.enabled" v-model="signal.join_type" row hide-details>
                <v-radio label="AND Signal" value="and" color="primary" hint="" persistent-hint />
                <v-radio label="OR Signal" value="or" color="primary" hint="" persistent-hint />
                <v-radio
                  v-if="signal.signal_id == 4 || signal.signal_id == 7 || signal.signal_id == 8"
                  label="Filter Other Signals"
                  value="filter"
                  color="primary"
                  hint=""
                  persistent-hint
                />
              </v-radio-group>
              <v-dialog v-model="stock_basket_dialog" persistent>
                <template #activator="{ props }">
                  <v-btn
                    v-if="signal.signal_id === 4"
                    :disabled="!signal.enabled || signal.join_type != 'filter'"
                    color="primary"
                    class="mt-4"
                    v-bind="props"
                  >
                    Add Optional Stock Basket
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="text-h5">
                    Create a basket of stocks that will always be evaluated.
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <!-- Optionally restrict to specific securities -->
                      <v-col cols="12" mb-6>
                        <helpers-section-sub-header class="mb-6">
                          These stocks will always be evaluated for your trends even if they don't
                          appear as a signal. Use this to force your model to use a basket of fixed
                          income ETFs for example as long as they show some sign of trending.
                        </helpers-section-sub-header>

                        <core-stock-manager
                          :key="'sm-' + current_trading_model.id"
                          v-model="signal.stock_basket"
                          flat
                          no-padding
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" text @click="stock_basket_dialog = false"> Save </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-window-item>

      <!-- Signals: Current View -->
      <v-window-item :value="true">
        <v-btn v-show="isSignalView" text color="primary" @click="backToAllSignals()">
          <v-icon left>
            {{ mdiKeyboardBackspace }}
          </v-icon>
          Back to All Signals
        </v-btn>
        <v-divider class="my-4" />
        <component :is="currentSignalComponent" />
        <v-divider class="my-4" />
        <v-btn v-show="isSignalView" text color="primary" @click="backToAllSignals()">
          <v-icon left>
            {{ mdiKeyboardBackspace }}
          </v-icon>
          Back to All Signals
        </v-btn>
      </v-window-item>
    </v-window>

    <template v-if="!isSignalView">
      <!-- Info note: Signal grouping -->
      <helpers-section-header>Signal grouping:</helpers-section-header>
      <div>All of your <b>AND</b> signals must be present to be picked.</div>
      <div>Any of your <b>OR</b> signals can be picked.</div>
      <div>
        <b>"Filter other signals"</b> means the signal will be used to filter other signal matches.
      </div>
      <br />
      <div>
        For example: You enable the 13F, Insider, and Trend signals. The 13F and Insider signals are
        set as OR Signals. The Trend Signal is set as an AND Signal. This means either the 13F or
        the Insider signal needs to be present and the Trend signal must always be present before
        being selected.
      </div>

      <!-- Info note: Signal Sorting and Prioritizing -->
      <helpers-section-header>Signal sorting and prioritizing</helpers-section-header>
      <v-row>
        <v-col cols="12">
          <v-radio-group
            v-model="whaleTraderCriteria.general.signal_ordering"
            :row="!$vuetify.display.xs"
            hide-details
            class="mt-1"
          >
            <v-radio :value="''" label="Signal Specific" color="primary" />
            <v-radio :value="'high'" label="1 month performance high to low" color="primary" />
            <v-radio :value="'low'" label="1 month performance low to high" color="primary" />
          </v-radio-group>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
// Modules

import { useGoTo } from 'vuetify'
import { mapWritableState } from 'pinia'
import { useAppStore } from '@/store'
import { markRaw } from 'vue'
// Components
import F13Signal from '@/components/page/whale-backtester/signals/_F13Signal'
import D13Signal from '@/components/page/whale-backtester/signals/_D13Signal'
import InsiderSignal from '@/components/page/whale-backtester/signals/_InsiderSignal'
import TrendSignal from '@/components/page/whale-backtester/signals/_TrendSignal2'
import WhaleMLSignal from '@/components/page/whale-backtester/signals/_WhaleMLSignal'
import WhaleIndexSignal from '@/components/page/whale-backtester/signals/_WhaleIndexSignal'
import FundamentalSignal from '@/components/page/whale-backtester/signals/_FundamentalSignal'
import F13IndicatorSignal from '@/components/page/whale-backtester/signals/_F13IndicatorSignal'
import WatchlistSignal from '@/components/page/whale-backtester/signals/_WatchlistSignal'
import { mdiKeyboardBackspace } from '@mdi/js'

export default {
  components: {
    F13Signal: markRaw(F13Signal),
    D13Signal: markRaw(D13Signal),
    InsiderSignal: markRaw(InsiderSignal),
    TrendSignal: markRaw(TrendSignal),
    WhaleMLSignal: markRaw(WhaleMLSignal),
    WhaleIndexSignal: markRaw(WhaleIndexSignal),
    FundamentalSignal: markRaw(FundamentalSignal),
    F13IndicatorSignal: markRaw(F13IndicatorSignal),
    WatchlistSignal: markRaw(WatchlistSignal)
  },

  setup() {
    const goTo = useGoTo()
    return { goTo }
  },

  data() {
    return {
      mdiKeyboardBackspace,
      isSignalView: false,
      currentSignalView: null,
      stock_basket_dialog: false
    }
  },
  computed: {
    ...mapWritableState(useAppStore, [
      'whaleTraderCriteria',
      'current_trading_model',
      'signalView',
      'app.buy_signal_frequencies'
    ]),
    ...mapWritableState(useAppStore, { criteria: 'whaleTraderCriteria' }),
    // ...mapWritableState(useUserStore, ["tradingModels", "sharedModels","trading_model_update",
    // "status_update_snackbar",'user', 'tradingModels']),
    /*...mapFields({
      whaleTraderCriteria: 'app.whaleTraderCriteria',
      current_trading_model: 'app.current_trading_model',
      // signalView: 'app.signalView',
      buy_signal_frequencies: 'app.buy_signal_frequencies'*/
    //}),
    selectedSignalsIndexes() {
      const signals = Object.values(this.whaleTraderCriteria.signals)

      return signals.filter(signal => signal.enabled).map(signal => signals.indexOf(signal))
    },
    currentSignalComponent() {
      const mapping = {
        1: 'F13Signal',
        2: 'D13Signal',
        3: 'InsiderSignal',
        4: 'TrendSignal',
        5: 'WhaleMLSignal',
        6: 'WhaleIndexSignal',
        7: 'FundamentalSignal',
        8: 'F13IndicatorSignal',
        9: 'WatchlistSignal'
      }

      return mapping[this.currentSignalView]
    }
  },
  methods: {
    setCurrentSignalView(id) {
      this.isSignalView = true
      this.currentSignalView = id
      setTimeout(() => this.goTo('#whale-backtester-page-current-model', { offset: 15 }), 400)
    },
    backToAllSignals() {
      this.isSignalView = false
      setTimeout(() => this.goTo('#whale-backtester-page-current-model', { offset: 15 }), 400)
    }
  }
}
</script>
